/**
 * VesselSettingsView - Step-based vessel configuration interface  
 * Modern, guided vessel setup with progressive disclosure
 * Mobile-optimized with expandable help system
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Menu, ChevronLeft, ChevronRight, Check, History } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { useAuditTrail } from '../../../hooks/useAuditTrail';
import VesselSettingsHistoryView from './VesselSettingsHistoryView';
import {
  isNonDisplayVesselSettingsField,
  VESSEL_SETTINGS_MIRROR_FIELDS_EXCLUDED_AT_WRITE_TIME,
} from './vesselSettingsAuditFields';

/**
 * Compute the set of meaningful changed fields between two vessel-config snapshots.
 * Returns { before, after, changedFields } where changedFields is keyed by field
 * name with { from, to } values. Ignores noise/internal fields and stringifies
 * for deep equality (good enough for vessel config which is a flat-ish object
 * with primitives + small arrays/objects).
 *
 * FT-515 round 8 (root-cause fix — read this before round 7's comment below):
 * round 7 filtered the UNION of before+after keys through a named-field
 * exclusion list. That only ever catches fields someone has already noticed
 * leaking (and a live review found at least one more slipping through:
 * `vessel_type`, a login/token-refresh-response field with no underscore
 * prefix and no schema entry). The actual save path, VesselContext's
 * updateVessel, MERGES the update into the existing record
 * (`{...vessel, ...updates}`) — it never clears a field just because that
 * field is absent from the update. So a field has no business being
 * considered "changed" unless it is actually part of THIS save's mapped
 * output. Iterating `Object.keys(after)` only (never the union with
 * `before`) makes that structural: any bookkeeping/login-response field that
 * only ever lives on the full live vessel object and is never produced by
 * mapConfigToVessel can no longer register as a false "changed to (empty)"
 * row, with no need to name it in advance.
 *
 * FT-515 round 7 (kept as a defensive backstop, no longer the primary
 * defense): the noise/internal-field exclusion comes from the SAME shared
 * catalogue VesselSettingsHistoryView.js uses to filter+label on display
 * (vesselSettingsAuditFields.js) — see that module's header comment.
 *
 * FT-515 round 8 (second fix, same file): `vesselName`/`vesselType`/
 * `homePort` are legitimately part of `after` on EVERY save (mapConfigToVessel
 * always mirrors them from name/type/port) but must never be compared as an
 * independent "change" — see VESSEL_SETTINGS_MIRROR_FIELDS_EXCLUDED_AT_WRITE_TIME's
 * doc comment in vesselSettingsAuditFields.js for exactly why this is a
 * SEPARATE, write-time-only set rather than joining isNonDisplayVesselSettingsField
 * (short version: folding them into the shared display-time filter would hide
 * genuine already-stored legacy entries that two existing regression tests
 * depend on).
 *
 * FT-515 round 9 (third fix, same file — read this after rounds 7/8 above):
 * even with round 8's Object.keys(after)-only iteration and mirror-field
 * exclusion, the diff still couldn't tell "this field was never set on the
 * existing record" apart from "this field was genuinely cleared/changed". A
 * REAL vessel record is often sparse — addVessel (VesselContext.js) doesn't
 * fill in schema defaults, AddVesselModal.js only writes a subset of fields,
 * and beforeSnapshotRef can even be `{}` if the vessels list hasn't loaded
 * yet when this view mounts. mapVesselToConfig, though, defaults EVERY
 * schema field when loading a sparse vessel into form state, and
 * mapConfigToVessel then writes all of those defaults back out on save —
 * so a no-op save on a sparse/freshly-added vessel produced a guaranteed
 * false "(empty) -> default" row for every field the vessel had never had
 * set (round 8's own test didn't catch this because it built "before" via a
 * full mapper round-trip first, which manufactures a fully-populated object
 * that never exercises this gap).
 *
 * Fix: when `before[key]` is `undefined` (truly absent — never set, not the
 * same as "set to empty") AND `after[key]` is exactly the mapper's own
 * default value for that field (getDefaultVesselSnapshot(), vesselConfigMapper.js
 * — the REAL mapVesselToConfig -> mapConfigToVessel round trip on an empty
 * vessel, not a second hand-written "what counts as empty" table), that's
 * "never existed" -> "exists at its default", not a real edit, so it's
 * skipped. A field that already had a genuine value in `before` (even a
 * default-looking one like `''`, because the user previously, genuinely,
 * cleared it) is NOT affected by this check — `before[key] !== undefined`
 * for that field, so it always falls through to the normal compare below,
 * and a real clear (e.g. port: 'Belfast' -> '') still logs correctly.
 *
 * FT-515 round 10 (fourth fix, same file — but NOT a change to this
 * function; computeVesselSettingsDiff itself is UNCHANGED below, the bug was
 * in its CALLER): round 9's reviewer found that none of the above matters if
 * the `before` this
 * function is CALLED with is stale relative to `after`. `beforeSnapshotRef`
 * (VesselSettingsView component, below) was captured exactly ONCE on mount
 * from `getCurrentVessel() || {}` — but the form itself (`vesselConfig`)
 * reloads from `getCurrentVessel()` any time `currentVesselId`/
 * `getCurrentVessel` change later (vessels list finishing its load, a cloud-
 * settings hydrate landing after login, a cross-device sync landing mid-
 * session). If real data arrives AFTER the screen opens, the FORM shows it
 * but `before` stays stuck on whatever existed (or didn't) at mount.
 * Combined with round 9's never-set-before bypass immediately above, a
 * genuine edit can get misread as "field was never set, now at its default"
 * and vanish from the audit log entirely — worse than round 9's noise bug,
 * this is silent data loss from a compliance audit trail.
 *
 * Fixed in the component below: the load effect now re-syncs
 * `beforeSnapshotRef.current` every time it re-runs, from the SAME
 * `vesselFromContext` and at the SAME moment the form loads it, via the new
 * `normalizeVesselForAudit` helper — which also closes the matching SHAPE
 * gap by running `before` through the exact same mapVesselToConfig ->
 * mapConfigToVessel -> applyAuditNormalization pipeline `handleSave` already
 * uses to build `after` (that pipeline is factored out of `handleSave` into
 * `applyAuditNormalization` and called from both places, rather than being a
 * second hand-copied version of handleSave's steps). computeVesselSettingsDiff
 * itself needed no change here — this was a caller-side timing/shape bug,
 * not a bug in the comparison logic.
 */
export const computeVesselSettingsDiff = (before = {}, after = {}) => {
  const changedFields = {};
  const defaultVessel = getDefaultVesselSnapshot();
  for (const key of Object.keys(after)) {
    if (isNonDisplayVesselSettingsField(key)) continue;
    if (VESSEL_SETTINGS_MIRROR_FIELDS_EXCLUDED_AT_WRITE_TIME.has(key)) continue;
    const b = before[key];
    const a = after[key];
    if (b === undefined && JSON.stringify(a) === JSON.stringify(defaultVessel[key])) {
      // Never set before, and still sitting at its default now -> not a
      // real change (see FT-515 round 9 comment above).
      continue;
    }
    if (JSON.stringify(b) !== JSON.stringify(a)) {
      changedFields[key] = { from: b, to: a };
    }
  }
  return { before, after, changedFields };
};

/**
 * FT-515 round 10: the exact normalization `handleSave` applies to a
 * freshly mapper-produced vessel-shape object before it becomes the audit
 * `after` snapshot — pulled out into its own function so `before` can be
 * put through the SAME steps (see `normalizeVesselForAudit` below) instead
 * of a second hand-copied version of this logic that could silently drift
 * from `handleSave`'s real behaviour in some future round. These are
 * `handleSave`'s actual normalization steps (verified by reading
 * `handleSave` itself, not guessed): flame-source-derived
 * `hasOpenFlame`/`openFlameProtection` are recomputed from
 * `galleyHeatSource` and coerced to real booleans; `engineBoxProtection`'s
 * sub-fields are likewise coerced to real booleans; `category` is mirrored
 * onto the legacy `areaCategory` field (and vice versa, in case only one of
 * the two is populated). Deliberately NOT included: `updatedAt` —
 * `handleSave` stamps that with `Date.now()`, which has no meaningful
 * equivalent for a `before` snapshot, and it's already excluded from the
 * diff entirely (`VESSEL_SETTINGS_NON_DISPLAY_FIELDS`), so omitting it here
 * is harmless.
 */
const applyAuditNormalization = (vesselUpdates = {}) => {
  const isOpenFlameSource = ['gas', 'open_flame', 'liquid_fuel'].includes(
    vesselUpdates.galleyHeatSource
  );
  const normalized = {
    ...vesselUpdates,
    hasOpenFlame: !!isOpenFlameSource,
    openFlameProtection: {
      blanket: !!vesselUpdates.openFlameProtection?.blanket,
      extraExtinguisher: !!vesselUpdates.openFlameProtection?.extraExtinguisher,
      fixedSystem: !!vesselUpdates.openFlameProtection?.fixedSystem,
    },
    engineBoxProtection: {
      firePort: !!vesselUpdates.engineBoxProtection?.firePort,
      fixedSystem: !!vesselUpdates.engineBoxProtection?.fixedSystem,
    },
  };
  const normalizedCat = normalized.category ?? normalized.areaCategory;
  return {
    ...normalized,
    category: normalizedCat,
    areaCategory: normalizedCat,
  };
};

/**
 * FT-515 round 10: build the audit `before` baseline from whatever
 * `getCurrentVessel()` hands back, run through the exact same
 * mapVesselToConfig -> mapConfigToVessel -> applyAuditNormalization pipeline
 * `handleSave` uses to build `after` — same SHAPE, and (critically, see the
 * component's load effect below) captured at the same TIME the form itself
 * loads that data. `vesselFromContext` can legitimately be `null`/`undefined`
 * (vessel not loaded yet) or `{}` (no record at all) — `mapVesselToConfig`
 * defaults every missing schema field in either case, so this always
 * returns a fully-shaped object, never `null`.
 */
const normalizeVesselForAudit = (vesselFromContext) => {
  const config = mapVesselToConfig(vesselFromContext || {}) || {};
  const vesselShape = mapConfigToVessel(config) || {};
  return applyAuditNormalization(vesselShape);
};
import { mapVesselToConfig, mapConfigToVessel, getDefaultVesselSnapshot } from './utils/vesselConfigMapper';
import StepVesselIdentity from './wizard/steps/StepVesselIdentity';
import StepClassification from './wizard/steps/StepClassification';
import StepPropulsion from './wizard/steps/StepPropulsion';
import StepSpaces from './wizard/steps/StepSpaces';
import StepSafetyEquipment from './wizard/steps/StepSafetyEquipment';
import StepSystems from './wizard/steps/StepSystems';
import StepDrillConfiguration from './wizard/steps/StepDrillConfiguration';
import CertificationSummary from './wizard/steps/components/CertificationSummary';
import EquipmentSelectionTab from './tabs/EquipmentSelectionTab';
import BasicInfoTab from './tabs/BasicInfoTab';
import OperationsTab from './tabs/OperationsTab';
import SafetyEquipmentTab from './tabs/SafetyEquipmentTab';
import VesselSpacesTab from './tabs/VesselSpacesTab';
import SystemsTab from './tabs/SystemsTab';
import RequirementsTab from './tabs/RequirementsTab';
import FullRequirementsReview from './tabs/FullRequirementsReview';
import DrillSettingsModal from '../../modules/DrillSettingsModal';
// EquipmentSetupWizard archived - no longer needed

// Import test suite and make it available globally
import { runAllTests } from './tabs/utils/vesselAutoConfig.test';
if (typeof window !== 'undefined') {
  window.testVesselConfig = runAllTests;
  
  // Debug helpers for vessel settings persistence audit (duplicate definition for wizard mode)
  if (!window.__debugVesselPersistence) {
    window.__debugVesselPersistence = () => {
      const vessel = JSON.parse(localStorage.getItem('vessel') || '{}');
      const targetFields = ['gmdssSeaArea', 'mobEquipment', 'lsaEquipment'];
      console.group('🔍 Vessel Settings Persistence Audit (Wizard Mode)');


      targetFields.forEach(field => {
        const value = vessel[field];

      });
      console.groupEnd();
      return { vessel, targetFields };
    };
  }
}

// Requirements Step Content Component with Tabs
const RequirementsStepContent = ({ vesselConfig }) => {
  const [activeTab, setActiveTab] = useState('equipment');
  
  const tabs = [
    {
      id: 'equipment',
      label: 'Equipment Selection', 
      icon: '🔧',
      description: 'Select and approve equipment for your vessel'
    },
    {
      id: 'complete',
      label: 'Complete Requirements',
      icon: '📋', 
      description: 'View all regulatory requirements'
    }
  ];
  
  return (
    <div className="space-y-6">
      <CertificationSummary draftConfig={vesselConfig} />
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'equipment' && (
          <EquipmentSelectionTab 
            vesselConfig={vesselConfig} 
            wizardMode={true}
            wizardData={vesselConfig}
          />
        )}
        {activeTab === 'complete' && (
          <FullRequirementsReview 
            vesselConfig={vesselConfig}
          />
        )}
      </div>
    </div>
  );
};

// Settings Steps Configuration
const SETTINGS_STEPS = [
  {
    id: 'identity',
    title: 'Vessel Identity',
    description: 'Basic vessel information and registration',
    shortTitle: 'Identity'
  },
  {
    id: 'classification',
    title: 'Classification',
    description: 'Area category, vessel type, and operating status',
    shortTitle: 'Classification'
  },
  {
    id: 'propulsion',
    title: 'Propulsion & Safety',
    description: 'Engine configuration and fire protection',
    shortTitle: 'Propulsion'
  },
  {
    id: 'spaces',
    title: 'Spaces & Accommodation',
    description: 'Galley, accommodation, and safety systems',
    shortTitle: 'Spaces'
  },
  {
    id: 'safety',
    title: 'Safety Equipment',
    description: 'Life-saving appliances and fire-fighting equipment',
    shortTitle: 'Safety'
  },
  {
    id: 'systems',
    title: 'Systems & Electrical',
    description: 'Navigation, communications, and electrical systems',
    shortTitle: 'Systems'
  },
  {
    id: 'drills',
    title: 'Drill Configuration',
    description: 'Review required drills and customize frequencies',
    shortTitle: 'Drills'
  },
  {
    id: 'requirements',
    title: 'Equipment Selection & Requirements',
    description: 'Select vessel equipment and review regulatory requirements',
    shortTitle: 'Equipment'
  }
];

const VesselSettingsView = ({ onBack }) => {
  const { currentVesselId, getCurrentVessel, updateVessel } = useVessel();
  // FT-Audit: vessel-settings audit log (under module name 'vessel_settings').
  // Storage key: audit_log_vessel_settings_<orgId>_<vesselId>.
  const { logChange } = useAuditTrail('vessel_settings');

  // FT-263 §5.9: capture the user's pre-edit canonical vessel state. Used by
  // handleSave as the audit `before` snapshot so a mid-edit cross-device
  // hydrate (which mutates the IDB-backed cache in VesselContext) cannot
  // corrupt the audit diff. Without this ref, getCurrentVessel() at
  // save-click could return Device B's pushed value instead of Device A's
  // pre-edit state.
  //
  // FT-515 round 10: NOT captured once-on-mount any more — see the load
  // effect below, where this ref is re-synced (via normalizeVesselForAudit)
  // every time that effect re-runs, from the exact same data the form
  // itself just loaded. A mount-only capture went stale whenever real vessel
  // data arrived after the screen opened (vessels list still loading, a
  // late cloud-settings hydrate, a cross-device sync mid-session), which
  // could make a genuine edit silently vanish from the audit log.
  const beforeSnapshotRef = useRef(null);

  const [showHistoryView, setShowHistoryView] = useState(false);
  const { showAlert, AlertDialog } = useCustomDialogs();
  
  // Step-based navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState({});
  const [showDrillSettings, setShowDrillSettings] = useState(false);
  
  // Equipment Setup Wizard state removed - archived
  
  // Setup Wizard state
  const [stepValidation, setStepValidation] = useState({});
  const [vesselConfig, setVesselConfig] = useState({
    // Basic vessel info
    vesselName: '',
    vesselType: '',
    category: '',
    lengthOverall: '',
    grossTonnage: '',
    draft: '',
    enginePower: '',
    age: '', // Vessel age in years for maintenance decisions
    timezone: '', // FT-269: vessel timezone (IANA name); empty string means "use device default"
    
    // Engine & Propulsion Details
    engineType: 'diesel', // 'diesel', 'petrol', 'outboard'
    enginePosition: 'below_deck', // 'below_deck', 'above_deck'
    numberOfEngines: 1,
    propulsionType: 'shaft', // 'shaft', 'jet', 'outboard'
    steeringType: 'hydraulic', // 'hydraulic', 'mechanical', 'electronic'
    
    // Capacity
    maxPersons: '',
    passengerCapacity: '',
    crewCount: 3,
    
    // Operating status for HOR compliance
    operatingStatus: 'seagoing', // Default to seagoing (more restrictive)
    
    // Equipment variations (not optional, just type selection)
    lifejacketType: 'foam', // 'foam' or 'inflatable'
    
    // Liferafts - only shown if required by area category
    liferafts_fitted: false,
    liferaft_capacity: '',
    hru_fitted: false,
    hruType: 'disposable', // 'disposable' or 'serviceable'
    
    // Optional boats (not required by regulations)
    lifeboat_fitted: false,
    rescue_boat_fitted: false,
    
    // Accommodation & Spaces
    hasAccommodation: false,
    hasGalley: false,
    hasEnclosedSpaces: false,
    enclosed_accommodation_present: false,
    cabin_accommodation: false,
    hasGasAppliances: false,
    hasLPGSystem: false,
    
    // Systems
    hasEmergencySteering: false,
    power_critical_systems: false,
    bilge_auto: false,
    hasShorepower: false,
    
    // Operations & Equipment Arrays
    operations: [],      // ['dangerous_goods', 'towing_operations', 'survey_operations']
    optionalEquipment: [], // Optional equipment: ['radar', 'gps', 'ais', 'echo_sounder', 'autopilot']
    specialEquipment: [], // ['winch_lifting_gear', 'gas_detection', 'crane', 'dynamic_positioning']
    
    // Environmental
    oilCapacityLitres: '',
    
    // Operations
    operatingHours: 'under_24h', // 'under_24h' or 'over_24h'
    internationalVoyages: false,
    towing_capable: false,
    engineer_onboard: false,
    
    // Equipment Check Settings
    equipmentCheckSettings: {
      lsaFrequency: 30,
      ffeFrequency: 30
    },
    
    // Company-specific drill requirements
    companyRequiredDrills: [],
    
    // CRITICAL: GMDSS and LSA Equipment wizard fields (persistence fix)
    gmdssSeaArea: '',
    mobEquipment: [], // MOB recovery selections from wizard
    lsaEquipment: [], // ['immersion_suits', 'thermal_protective_aids', 'line_throwing_apparatus']
    
    // Certificates - with dates for tracking
    certificates: {
      workboatCode: { number: '', expiry: '' },
      smallVesselCert: { number: '', expiry: '' },
      loadLine: { number: '', expiry: '' },
      safetyConstruction: { number: '', expiry: '' },
      safetyEquipment: { number: '', expiry: '' },
      safetyRadio: { number: '', expiry: '' },
      iopp: { number: '', expiry: '' },
      insurance: { number: '', expiry: '' }
    }
  });

  const handleNavigation = (moduleId) => {
    if (moduleId === 'settings') {
      // Already in settings
      return;
    }
    
    // Close settings and navigate to module
    setActiveModule(moduleId);
    onBack();
  };

  // Load vessel data when vessel changes
  //
  // FT-515 round 10: beforeSnapshotRef is now synced HERE, inside this same
  // effect, from the SAME vesselFromContext the form is loading — not in a
  // separate mount-only effect. This is the actual fix: previously the form
  // could reload later (vessels list finishing its load, a late cloud
  // hydrate, a cross-device sync) while `before` stayed frozen at whatever
  // existed at mount, so a genuine edit could be compared against stale (or
  // absent) prior data and misread as "never set, still at default" by
  // computeVesselSettingsDiff's round-9 bypass — silently dropping it from
  // the audit log. Keeping both captures in one effect makes that
  // structurally impossible: there is no code path where the form loads new
  // data without `before` being re-synced to match it, at the same instant,
  // from the same source object.
  useEffect(() => {

    if (currentVesselId) {
      const vesselFromContext = getCurrentVessel();

      const configFromVessel = mapVesselToConfig(vesselFromContext);

      if (configFromVessel) {
        setVesselConfig(configFromVessel);

        // FT-515 round 11: moved inside this same `if` so `before` and the
        // form only ever update together. If vesselFromContext/configFromVessel
        // is null (vessel briefly unresolvable — e.g. mid-hydrate, or deleted
        // on another device), beforeSnapshotRef.current is left untouched
        // rather than being overwritten with normalizeVesselForAudit(null)'s
        // all-defaults object, which previously caused `before` to drift away
        // from the form even though the form itself didn't change.
        //
        // Normalized through the SAME mapVesselToConfig -> mapConfigToVessel
        // -> applyAuditNormalization pipeline handleSave uses for `after`, so
        // the diff compares like-with-like in both timing AND shape.
        beforeSnapshotRef.current = normalizeVesselForAudit(vesselFromContext);
      }
    }
  }, [currentVesselId, getCurrentVessel]); // Re-run when vessel data changes

  // FT-515 round 10: the separate mount-only `beforeSnapshotRef` capture that
  // used to live here was the bug — it never re-ran when the load effect
  // above re-ran with newer data, so `before` went stale. Removed; the load
  // effect above now owns beforeSnapshotRef's only write, synced to the same
  // data/moment the form loads.

  // Listen for drill settings event from drill configuration step
  useEffect(() => {
    const handleOpenDrillSettings = () => {
      setShowDrillSettings(true);
    };
    
    window.addEventListener('openDrillSettings', handleOpenDrillSettings);
    return () => {
      window.removeEventListener('openDrillSettings', handleOpenDrillSettings);
    };
  }, []);

  // Handle direct step navigation from sessionStorage
  useEffect(() => {
    const savedStep = sessionStorage.getItem('vesselSettingsStep');
    if (savedStep) {
      const stepNumber = parseInt(savedStep, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 8) {
        setCurrentStep(stepNumber - 1); // Convert to 0-indexed
      }
      sessionStorage.removeItem('vesselSettingsStep');
    }
  }, []); // Run once on mount

  // Detect if opened from Equipment Register "Add Equipment" button
  useEffect(() => {
    const contextData = sessionStorage.getItem('addEquipmentContext');

    if (contextData) {
      try {
        const context = JSON.parse(contextData);

        if (context.returnTo === 'equipment-register') {

          // Step 8 is index 7 (0-indexed)
          setCurrentStep(7);

          // Don't clear context yet - EquipmentSelectionTab needs it for guidance banner

        }
      } catch (error) {

        sessionStorage.removeItem('addEquipmentContext');
      }
    }
  }, []); // Run once on mount

  const handleSave = async () => {
    // Basic validation
    if (!vesselConfig.vesselName || !vesselConfig.vesselType || vesselConfig.category === '') {
      await showAlert(
        'Required Fields Missing',
        'Please complete all required fields: Name, Type, and Category',
        'warning'
      );
      return;
    }

    // Update VesselContext as the primary source of truth
    if (currentVesselId) {
      const vesselUpdates = mapConfigToVessel(vesselConfig);

      // 🔒 Normalize flame-related fields so they always persist correctly,
      // and mirror category <-> areaCategory — FT-515 round 10: factored out
      // into applyAuditNormalization (shared with normalizeVesselForAudit,
      // used for the audit `before` snapshot in the load effect above) so
      // both snapshots go through literally the same normalization code.
      const normalized = applyAuditNormalization(vesselUpdates);

      const finalVesselUpdates = {
        ...normalized,
        updatedAt: Date.now()          // immutability to trigger re-renders
      };

      // FT-Audit: capture before snapshot BEFORE updateVessel commits, then
      // compute diff. We only write an audit entry when meaningful fields
      // actually changed (computeVesselSettingsDiff strips noise like
      // updatedAt + legacy mirrors). Saves with no real changes don't pollute
      // the log.
      // FT-263 §5.9: use the ref (kept in sync by the load effect above —
      // see FT-515 round 10), NOT a fresh getCurrentVessel() call at click
      // time. The IDB-backed cache in VesselContext can be mutated by a
      // cross-device hydrate mid-edit, which would silently corrupt the audit
      // diff (comparing this user's after-edit state to Device B's pushed
      // value instead of this user's true pre-edit state).
      const beforeSnapshot = beforeSnapshotRef.current || {};
      const diff = computeVesselSettingsDiff(beforeSnapshot, finalVesselUpdates);

      updateVessel(currentVesselId, finalVesselUpdates);

      if (Object.keys(diff.changedFields).length > 0) {
        // Fire-and-forget audit write — failure shouldn't block the save UX.
        logChange({
          action: 'update',
          item: {
            id: currentVesselId,
            name: beforeSnapshot.vesselName || beforeSnapshot.name || finalVesselUpdates.vesselName || 'Vessel',
            type: 'vessel_settings',
          },
          reason: { code: 'edit', label: 'Vessel settings updated' },
          changes: diff,
        }).catch(err => {
          console.warn('[VesselSettingsView] Audit log write failed (non-fatal):', err);
        });
      }

      // Trigger drill configuration save if there are pending changes
      if (vesselConfig.drillConfigPending) {
        setVesselConfig(prev => ({
          ...prev,
          saveDrillChanges: true
        }));
        
        // Clear the save flag after a brief delay
        setTimeout(() => {
          setVesselConfig(prev => ({
            ...prev,
            saveDrillChanges: false,
            drillConfigPending: false
          }));
        }, 100);
      }
      
      // Show success message
      await showAlert(
        'Settings Saved',
        'Vessel configuration has been updated successfully.',
        'success'
      );

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
    }

    // FT-558: do NOT navigate away after a successful save. Jonathan wants
    // the user to stay on the Vessel Settings screen (with the "Settings
    // Saved" confirmation above) so they can keep editing or explicitly
    // leave via the dedicated Close button / mobile X control in the header
    // below. Previously this function ended with an unconditional onBack()
    // call, which silently kicked the user back to the Dashboard right
    // after every save.
  };


  // Create updateConfig function to match what BasicInfoTab expects
  // Updates React state ONLY - no storage persistence until Save button clicked
  const updateConfig = (updates) => {
    setHasUnsavedChanges(true); // Mark as dirty whenever config changes
    if (typeof updates === 'function') {
      setVesselConfig(prev => updates(prev));
    } else {
      setVesselConfig(prev => ({ ...prev, ...updates }));
    }
  };

  // Handle step validation updates
  const handleStepValidation = useCallback((stepId, validationResult) => {
    setStepValidation(prev => ({
      ...prev,
      [stepId]: validationResult
    }));
  }, []); // Empty deps - setStepValidation is stable, stepId and validationResult come from callback params

  // Step navigation helpers
  const currentStepData = SETTINGS_STEPS[currentStep];
  const progress = ((currentStep + 1) / SETTINGS_STEPS.length) * 100;
  
  const handleNext = async () => {
    // Check if current step has validation and if it's valid
    const currentStepId = SETTINGS_STEPS[currentStep].id;
    const validation = stepValidation[currentStepId];
    
    if (validation && !validation.isValid) {
      // Show warning about missing required selections
      const missingItems = validation.missingChoices.map(choice => choice.label).join(', ');
      await showAlert(
        'Required Selections Missing',
        `Please complete the following required selections before proceeding: ${missingItems}`,
        'warning'
      );
      return;
    }
    
    if (currentStep < SETTINGS_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };
  
  const toggleHelp = (sectionId) => {
    setExpandedHelp(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: // Identity
        return <StepVesselIdentity draftConfig={vesselConfig} onUpdate={updateConfig} />;
      case 1: // Classification  
        return <StepClassification draftConfig={vesselConfig} onUpdate={updateConfig} />;
      case 2: // Propulsion
        return <StepPropulsion draftConfig={vesselConfig} onUpdate={updateConfig} />;
      case 3: // Spaces
        return <StepSpaces draftConfig={vesselConfig} onUpdate={updateConfig} />;
      case 4: // Safety Equipment
        return (
          <StepSafetyEquipment 
            draftConfig={vesselConfig} 
            onUpdate={updateConfig} 
            onValidation={(validation) => handleStepValidation('safety', validation)}
          />
        );
      case 5: // Systems
        return <StepSystems draftConfig={vesselConfig} onUpdate={updateConfig} />;
      case 6: // Drill Configuration
        return <StepDrillConfiguration vesselConfig={vesselConfig} onChange={updateConfig} />;
      case 7: // Requirements & Equipment Selection
        return (
          <RequirementsStepContent 
            vesselConfig={vesselConfig}
          />
        );
      default:
        return null;
    }
  };

  // FT-Audit: full-screen history view takes precedence when active.
  if (showHistoryView) {
    return <VesselSettingsHistoryView onBack={() => setShowHistoryView(false)} />;
  }

  return (
    <>
      {/* Mobile Step Navigation Sidebar */}
      <div className={`lg:hidden fixed inset-0 z-40 ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSidebarOpen(false)} />
        <div className="fixed left-0 top-0 bottom-0 w-80 bg-white border-r border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Vessel Settings</h2>
            <p className="text-sm text-gray-600">Configure your vessel step by step</p>
          </div>
          
          <div className="px-6 py-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Step {currentStep + 1} of {SETTINGS_STEPS.length}
            </p>
          </div>

          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            {SETTINGS_STEPS.map((step, index) => {
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              const validation = stepValidation[step.id];
              const hasValidationIssues = validation && !validation.isValid;

              return (
                <button
                  key={step.id}
                  onClick={() => {
                    handleStepClick(index);
                    setSidebarOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? hasValidationIssues
                        ? 'bg-yellow-100 text-yellow-900 border border-yellow-300'
                        : 'bg-cyan-100 text-cyan-900 border border-cyan-200'
                      : isCompleted
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  title={hasValidationIssues 
                    ? `Missing required selections: ${validation.missingChoices.map(choice => choice.label).join(', ')}`
                    : undefined
                  }
                >
                  <div className="flex items-center space-x-3">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-100 border-green-300'
                        : isActive && hasValidationIssues
                        ? 'bg-yellow-100 border-yellow-400'
                        : isActive
                        ? 'border-cyan-300'
                        : 'border-gray-300'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : hasValidationIssues ? (
                        <span className="text-xs text-yellow-600">⚠</span>
                      ) : (
                        <span className="text-xs">{index + 1}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{step.shortTitle}</div>
                      <div className="text-xs text-gray-500">{step.description}</div>
                      {hasValidationIssues && (
                        <div className="text-xs text-yellow-600 font-medium mt-1">
                          Missing required selections
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Step-based Settings Interface */}
      <div className="min-h-screen bg-gray-50 flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex lg:flex-col lg:w-80 bg-white border-r border-gray-200">
          {/* Sidebar Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Vessel Settings</h2>
            <p className="text-sm text-gray-600">Configure your vessel step by step</p>
          </div>
          
          {/* Progress Bar */}
          <div className="px-6 py-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Step {currentStep + 1} of {SETTINGS_STEPS.length}
            </p>
          </div>

          {/* Step Navigation */}
          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            {SETTINGS_STEPS.map((step, index) => {
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              const validation = stepValidation[step.id];
              const hasValidationIssues = validation && !validation.isValid;

              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(index)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? hasValidationIssues
                        ? 'bg-yellow-100 text-yellow-900 border border-yellow-300'
                        : 'bg-cyan-100 text-cyan-900 border border-cyan-200'
                      : isCompleted
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  title={hasValidationIssues 
                    ? `Missing required selections: ${validation.missingChoices.map(choice => choice.label).join(', ')}`
                    : undefined
                  }
                >
                  <div className="flex items-center space-x-3">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-100 border-green-300'
                        : isActive && hasValidationIssues
                        ? 'bg-yellow-100 border-yellow-400'
                        : isActive
                        ? 'border-cyan-300'
                        : 'border-gray-300'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : hasValidationIssues ? (
                        <span className="text-xs text-yellow-600">⚠</span>
                      ) : (
                        <span className="text-xs">{index + 1}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{step.shortTitle}</div>
                      <div className="text-xs text-gray-500">{step.description}</div>
                      {hasValidationIssues && (
                        <div className="text-xs text-yellow-600 font-medium mt-1">
                          Missing required selections
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Mobile Header */}
          <div className="lg:hidden px-4 py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{currentStepData.title}</h2>
                  <p className="text-sm text-gray-600">Step {currentStep + 1} of {SETTINGS_STEPS.length}</p>
                </div>
              </div>
              <button
                onClick={onBack}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile Progress */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-cyan-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:flex px-6 py-4 border-b border-gray-200 bg-white items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{currentStepData.title}</h1>
              <p className="text-gray-600">{currentStepData.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              {/* FT-Audit: vessel settings change history (audit log of who
                  changed what fields and when). Opens a diff-style viewer.
                  Storage layer mirrors equipment ChangeLogViewer's pattern. */}
              <button
                onClick={() => setShowHistoryView(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                title="View vessel settings change history"
              >
                <History className="h-4 w-4" />
                History
              </button>
              <button
                onClick={handleSave}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors ${
                  hasUnsavedChanges
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-cyan-600 hover:bg-cyan-700'
                }`}
              >
                <Save className="h-4 w-4" />
                {hasUnsavedChanges ? 'Save Changes *' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {renderCurrentStep()}
          </div>

          {/* Navigation Footer */}
          <div className="px-4 py-4 lg:px-6 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                  currentStep === 0
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </button>

              <div className="lg:hidden text-sm text-gray-500">
                {currentStep + 1} of {SETTINGS_STEPS.length}
              </div>

              <button
                onClick={handleNext}
                disabled={currentStep === SETTINGS_STEPS.length - 1}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentStep === SETTINGS_STEPS.length - 1
                    ? 'text-gray-400 border border-gray-200 cursor-not-allowed'
                    : (() => {
                        const currentStepId = SETTINGS_STEPS[currentStep].id;
                        const validation = stepValidation[currentStepId];
                        const hasValidationIssues = validation && !validation.isValid;
                        return hasValidationIssues
                          ? 'text-white bg-yellow-600 hover:bg-yellow-700 border-2 border-yellow-400'
                          : 'text-white bg-cyan-600 hover:bg-cyan-700';
                      })()
                }`}
                title={(() => {
                  const currentStepId = SETTINGS_STEPS[currentStep].id;
                  const validation = stepValidation[currentStepId];
                  if (validation && !validation.isValid) {
                    const missingItems = validation.missingChoices.map(choice => choice.label).join(', ');
                    return `Missing required selections: ${missingItems}`;
                  }
                  return 'Continue to next step';
                })()}
              >
                {(() => {
                  const currentStepId = SETTINGS_STEPS[currentStep].id;
                  const validation = stepValidation[currentStepId];
                  return validation && !validation.isValid ? '⚠️ Next (Missing Required)' : 'Next';
                })()}
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Dialog */}
      <AlertDialog />
      
      {/* Drill Settings Modal */}
      {showDrillSettings && (
        <DrillSettingsModal 
          isOpen={showDrillSettings}
          onClose={() => setShowDrillSettings(false)}
        />
      )}
      
      {/* Equipment Setup Wizard component removed - archived */}
    </>
  );
};

export default VesselSettingsView;